import type { ComponentProps } from 'react'
import { HeaderBar } from '@/components/commons/header-bar'
import { useAuthStore } from '@/features/auth/store/store.ts'
import { getLuxeRedirectPath } from '@/features/luxe-submissions/utils'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

/**
 * The app header for the Luminate side of the app, showing the signed-in
 * reviewer.
 *
 * One of the two adapters over [[HeaderBar]], which owns the markup and knows
 * nothing about identity. This one reads the auth store (populated by `useMe`,
 * which runs on `LuminateSessionRoute`) and words the reviewer's role from its
 * own vocabulary; `MarqueeHeader` is the other, over an identity this store
 * never holds.
 *
 * Two thin adapters rather than two headers on purpose: it is the same bar, and
 * only two facts differ, who is signed in and where the logo goes back to.
 */
export const Header = (
	props: Omit<ComponentProps<typeof HeaderBar>, 'user' | 'homeTo'>
) => {
	const { t } = useTranslation()
	const reviewer = useAuthStore((state) => state.user)

	return (
		<HeaderBar
			{...props}
			homeTo={getLuxeRedirectPath(reviewer?.role) ?? routes.WELCOME.path}
			user={
				reviewer
					? {
							name: reviewer.first_name ?? reviewer.email,
							email: reviewer.email,
							roleLabel: reviewer.role
								? t(`auth:roles.${reviewer.role}`)
								: undefined
						}
					: undefined
			}
		/>
	)
}

Header.displayName = 'Header'
